import React, { createContext, useState, useContext, useEffect } from 'react';
import notificationService from '../services/notificationService';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

const KEYS = ['token', 'userId', 'emailVerified'];

// Read a value from whichever storage currently holds the session.
// localStorage (remembered) takes precedence over sessionStorage.
const readAuth = (key) =>
  localStorage.getItem(key) ?? sessionStorage.getItem(key);

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(() => readAuth('token'));
  const [userId, setUserId] = useState(() => readAuth('userId'));
  const [emailVerified, setEmailVerified] = useState(() => readAuth('emailVerified') === 'true');

  // Persist session changes into the active storage. We keep writing to the
  // storage that already holds the token so the "remember me" choice sticks.
  useEffect(() => {
    const store = localStorage.getItem('token') !== null ? localStorage
      : sessionStorage.getItem('token') !== null ? sessionStorage
      : null;
    if (!store || token === null) return;
    store.setItem('token', token);
    if (userId) store.setItem('userId', userId);
    store.setItem('emailVerified', String(emailVerified));
  }, [token, userId, emailVerified]);

  const clearAllStorage = () => {
    KEYS.forEach((k) => {
      localStorage.removeItem(k);
      sessionStorage.removeItem(k);
    });
  };

  /**
   * @param remember when true, persist across browser restarts (localStorage);
   *                 when false, session-only (cleared when the tab/browser closes).
   */
  const login = (authToken, id, verified = false, remember = true) => {
    // Start clean so a stale token never lingers in the other storage.
    clearAllStorage();

    const store = remember ? localStorage : sessionStorage;
    store.setItem('token', authToken);
    store.setItem('userId', String(id));
    store.setItem('emailVerified', String(verified));

    setToken(authToken);
    setUserId(String(id));
    setEmailVerified(verified);

    // Request notification permission a moment after login.
    setTimeout(() => {
      if (!notificationService.isNotificationSupported()) return;
      const permission = notificationService.getPermissionStatus();
      if (permission === 'default' || permission === 'granted') {
        notificationService.requestPermission().catch(() => {});
      }
    }, 2000);
  };

  const logout = () => {
    notificationService.unregisterToken().catch(() => {});
    clearAllStorage();
    setToken(null);
    setUserId(null);
    setEmailVerified(false);
  };

  const updateEmailVerified = (verified) => {
    setEmailVerified(verified);
    const store = localStorage.getItem('token') !== null ? localStorage : sessionStorage;
    store.setItem('emailVerified', String(verified));
  };

  return (
    <AuthContext.Provider value={{ token, userId, emailVerified, login, logout, updateEmailVerified }}>
      {children}
    </AuthContext.Provider>
  );
};
